import type { ReactNode } from 'react'

import { BRAND_ID, type BrandId } from 'config/brands'
import { PRODUCTS } from 'utils/enums'

/**
 * The products each brand offers. Keep in sync with PRODUCTS_FOR_BRAND in
 * server/src/shared/config/constants/shared.ts. Used to restrict voucher
 * creation to products the selected brand actually offers (e.g. Simplicity
 * does not offer probate).
 */
export const VOUCHER_PRODUCTS_FOR_BRAND: Record<BrandId, string[]> = {
  [BRAND_ID.dignity]: [PRODUCTS.PROBATE],
  [BRAND_ID.farewill]: [
    PRODUCTS.FUNERAL,
    PRODUCTS.FUNERAL_PLAN,
    PRODUCTS.LPA,
    PRODUCTS.PROBATE,
    /** Not in PRODUCTS enum as FE usually does not have to differentiate between
     * online and telephone wills, given that it only handles telephone wills. */
    'online_will',
    'telephone_will',
  ],
  [BRAND_ID.simplicity]: [PRODUCTS.FUNERAL, PRODUCTS.FUNERAL_PLAN],
}

export const VOUCHER_PRODUCT_OPTIONS = [
  { label: 'Funeral', value: PRODUCTS.FUNERAL },
  { label: 'Funeral plan', value: PRODUCTS.FUNERAL_PLAN },
  { label: 'LPA', value: PRODUCTS.LPA },
  { label: 'Online will', value: 'online_will' },
  { label: 'Probate', value: PRODUCTS.PROBATE },
  { label: 'Telephone will', value: 'telephone_will' },
]

/**
 * Returns the product options available for a given brand, preserving the
 * order and labels of VOUCHER_PRODUCT_OPTIONS. Returns an empty array when no
 * brand is selected.
 */
export const getVoucherProductsForBrand = (
  brandId: BrandId
): { label: string; value: string }[] => {
  if (!brandId) return []

  const allowedProducts = VOUCHER_PRODUCTS_FOR_BRAND[brandId] ?? []

  return VOUCHER_PRODUCT_OPTIONS.filter((option) =>
    allowedProducts.includes(option.value)
  )
}

/**
 * Keep in sync with server/src/shared/utils/voucher-messages.ts — the server
 * version is canonical for purchase-flow messages.
 */
const PRODUCT_TO_HUMAN: Record<string, string> = {
  funeral: 'funeral',
  funeral_plan: 'plan',
  lpa: 'LPA',
  probate: 'probate case',
  online_will: 'online will',
  telephone_will: 'telephone will',
}

export const getHumanProductName = (productName: string): string =>
  PRODUCT_TO_HUMAN[productName] ?? 'plan'

export const getGiftCardOfferMessageText = ({
  partnerName,
  offer,
  productName,
}: {
  partnerName: string
  offer: string
  productName: string
}): string | null => {
  if (!partnerName || !offer) return null

  return `Thanks to ${partnerName} you’ll get ${offer} with this ${getHumanProductName(
    productName
  )}.`
}

export const getGiftCardOfferMessage = ({
  partnerName,
  offer,
  productName,
}: {
  partnerName: string
  offer: string
  productName: string
}): ReactNode => {
  const productLabel = getHumanProductName(productName)

  if (!partnerName || !offer) {
    return (
      <>
        Thanks to <strong>[partner name]</strong> you’ll get{' '}
        <strong>[offer description]</strong> with this {productLabel}. T&Cs
        apply.
      </>
    )
  }

  return (
    <>
      Thanks to <strong>{partnerName}</strong> you’ll get{' '}
      <strong>{offer}</strong> with this {productLabel}. T&Cs apply.
    </>
  )
}
